import {Dispatch, SetStateAction, useState} from 'react';

import {OtpTypeCode} from '@/consts/loginTypes';

import useOtpLoginHandlers, {OtpIdentifierSubmitParams} from '../../hooks/useOtpLoginHandlers';
import type {LoginErrorDetails} from '../../LoginPageV2.types';
import OtpCodeView from './OtpCodeView';
import OtpIdentifierSection from './OtpIdentifierSection';

interface OtpLoginSectionProps {
    otpChannel: OtpTypeCode;
    otpChannels: OtpTypeCode[];
    hasOtpTabs: boolean;
    isOtpCodeStep: boolean;
    selectOtpChannel: (channel: OtpTypeCode) => void;
    goToOtpCodeStep: () => void;
    resetOtpFlow: () => void;
    loginError: LoginErrorDetails;
    setLoginError: Dispatch<SetStateAction<LoginErrorDetails>>;
    isLoginLanguageSelectEnabled?: boolean;
}

interface OtpInfo extends OtpIdentifierSubmitParams {
    channel: OtpTypeCode;
}

const OtpLoginSection = ({
    otpChannel,
    otpChannels,
    hasOtpTabs,
    isOtpCodeStep,
    selectOtpChannel,
    goToOtpCodeStep,
    resetOtpFlow,
    loginError,
    setLoginError,
    isLoginLanguageSelectEnabled,
}: OtpLoginSectionProps) => {
    const [otpInfo, setOtpInfo] = useState<OtpInfo | null>(null);

    const {handleOtpIdentifierSubmit, handleOtpCodeComplete, handleOtpBack} = useOtpLoginHandlers({
        otpChannel,
        goToOtpCodeStep,
        resetOtpFlow,
        setLoginError,
    });

    const handleOtpIdentifierSubmitWithStore = async ({
        identifier,
        phoneCountryCode,
        showFullscreenLoader,
    }: OtpIdentifierSubmitParams) => {
        const nextOtpInfo: OtpInfo = {
            phoneCountryCode,
            channel: otpChannel,
            identifier,
        };

        setOtpInfo(nextOtpInfo);
        await handleOtpIdentifierSubmit({
            identifier: nextOtpInfo.identifier,
            phoneCountryCode: nextOtpInfo.phoneCountryCode,
            showFullscreenLoader,
        });
    };

    const handleOtpResend = async () => {
        if (!otpInfo) return;

        await handleOtpIdentifierSubmitWithStore({
            identifier: otpInfo.identifier,
            phoneCountryCode: otpInfo.phoneCountryCode,
            showFullscreenLoader: false,
        });
    };

    if (isOtpCodeStep) {
        if (!otpInfo) {
            return null;
        }

        return (
            <OtpCodeView
                info={otpInfo}
                onComplete={handleOtpCodeComplete}
                onResend={handleOtpResend}
                onBack={handleOtpBack}
                isLoginLanguageSelectEnabled={isLoginLanguageSelectEnabled}
            />
        );
    }

    return (
        <OtpIdentifierSection
            channel={otpChannel}
            hasTabs={hasOtpTabs}
            channels={otpChannels}
            error={loginError}
            setError={setLoginError}
            onChannelChange={(nextOtpChannel) => {
                setLoginError(null);
                selectOtpChannel(nextOtpChannel);
            }}
            onSubmit={handleOtpIdentifierSubmitWithStore}
            isLoginLanguageSelectEnabled={isLoginLanguageSelectEnabled}
        />
    );
};

export default OtpLoginSection;
